
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle nav background on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-serif">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/9f4ee01b-9e0a-4c3a-81f6-f6867df1cc30.png"
              alt="Tory Stephens"
              className="h-12 w-12 rounded-full object-cover cursor-pointer"
            />
          </Link>
          <div className="flex items-center gap-8">
            <Link
              to="/about"
              className="text-sm tracking-wide text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/#selected-work"
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Selected Work
            </Link>
            <a
              href="https://bsky.app"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Bluesky
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              LinkedIn
            </a>
            <a
              href="#contact"
              className="text-sm tracking-wide text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get in touch
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-44 pb-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-gray-500 mb-10">About</p>
          <h1 className="text-5xl md:text-6xl font-light leading-tight text-gray-900 mb-14">
            Stories rooted in justice, hope, and truth.
          </h1>
          <p className="text-xl leading-relaxed text-gray-700 max-w-2xl">
            I&rsquo;m Tory Stephens—a storyteller, strategist, and climate justice advocate. I work at the
            intersection of culture, justice, and climate futures, helping stories travel beyond policy
            circles and into the imaginations of everyday people.
          </p>
        </div>
      </section>

      {/* Background */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-10">
            <a href="#background" id="background" className="hover:text-gray-700 transition-colors cursor-pointer">
              Background
            </a>
          </h4>
          <div className="space-y-8 max-w-2xl">
            <p className="text-lg leading-relaxed text-gray-800">
              My path has always been shaped by the belief that narrative is power. Before entering the climate
              space, I spent years exploring how stories—whether told through fiction, journalism, or community
              organizing—can shift culture and open new possibilities for collective action.
            </p>
            <p className="text-lg leading-relaxed text-gray-800">
              That belief led me to Grist, where I co-founded{' '}
              <strong className="font-medium text-gray-900">Imagine 2200</strong>, a climate fiction initiative
              designed for future ancestors. Through Imagine 2200, I&rsquo;ve helped produce two anthologies—{' '}
              <em>Afterglow</em> and <em>Metamorphosis</em>—and now focus on publishing climate fiction
              year-round while building partnerships that connect narrative, movement work, and real-world
              climate solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-10">
            <a href="#philosophy" id="philosophy" className="hover:text-gray-700 transition-colors cursor-pointer">
              Philosophy
            </a>
          </h4>
          <div className="space-y-8 max-w-2xl">
            <p className="text-lg leading-relaxed text-gray-800">
              I believe{' '}
              <strong className="font-medium text-gray-900">culture is infrastructure</strong>. The stories we
              tell—through fiction, art, and media—shape what futures feel possible, who belongs in them, and
              how we act in the present.
            </p>
            <p className="text-lg leading-relaxed text-gray-800">
              This isn’t about escapism. It’s about building the narrative architecture for a just transition.
              When communities see themselves reflected in visions of the future, they&rsquo;re more likely to
              fight for that future today.
            </p>
          </div>
        </div>
      </section>

      {/* How I Work */}
      <section className="py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-10">
            <a href="#how-i-work" id="how-i-work" className="hover:text-gray-700 transition-colors cursor-pointer">
              How I Work
            </a>
          </h4>
          <div className="space-y-16 max-w-2xl">
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Storytelling &amp; Publishing</h3>
              <p className="text-lg leading-relaxed text-gray-800">
                I commission, edit, and publish climate fiction that centers justice, joy, and possibility.
                Through Imagine 2200 and beyond, I work with writers from around the world to create narratives
                that expand our collective imagination.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Strategy &amp; Partnerships</h3>
              <p className="text-lg leading-relaxed text-gray-800">
                I build bridges between the climate movement, cultural institutions, and creative communities.
                This means designing partnerships that connect narrative power with on-the-ground organizing and
                policy work.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Speaking &amp; Facilitation</h3>
              <p className="text-lg leading-relaxed text-gray-800">
                I speak and facilitate conversations about the role of culture in climate justice—at conferences,
                universities, and community spaces. I&rsquo;m interested in how we can use storytelling as a
                tool for movement building.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-40 px-6">
        <div className="max-w-3xl mx-auto">
          <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-10">
            <a href="#contact" className="hover:text-gray-700 transition-colors cursor-pointer">
              Get in Touch
            </a>
          </h4>
          <p className="text-lg leading-relaxed text-gray-800 mb-10 max-w-2xl">
            For speaking, collaborations, or conversations about climate storytelling:
          </p>
          <a
            href="mailto:toryjstephens@gmail.com"
            className="inline-block text-xl text-gray-900 hover:text-gray-600 transition-colors border-b-2 border-gray-900 hover:border-gray-600 pb-1 cursor-pointer whitespace-nowrap"
          >
            toryjstephens@gmail.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-sm text-gray-600">
            &copy; {new Date().getFullYear()} Tory Stephens. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
